import { useState, useEffect, useRef, useCallback, memo, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { v4 as uuidv4 } from "uuid";
import CommandCenterService from "../../../../service/CommandCenterService";
import useUrlParams from "../../hooks/useUrlParams";
import useDashboard from "../../hooks/useDashboard";
import { getEnvVar } from "../../../../envUtils";
import { get_formated_time } from "../../../command_center/components/center/modals/console/Utils";
import { ArrowUp } from "../../../command_center/components/center/modals/console/ConsoleSvg";
import CustomToolTip from "../../../CommonComponents/CustomToolTip";
import ShimmerMenuList from "../../../CommonComponents/SimmerEffectDesign/ShimmerMenu";
import "../../../command_center/components/center/modals/console/Console.scss";

// Same payload as command_center RawOuputList
const RAW_BYTES_PAYLOAD = {
  TMHeaderTimestampRange: { IsFilterApplied: false, FromTime: -1, ToTime: -1 },
  SaIdEqualFilter: [],
  SaIdNotEqualFilter: [],
  DaIdEqualFilter: [],
  DaIdNotEqualFilter: [],
  TcTmIdEqualFilter: [],
  TcTmIdNotEqualFilter: [],
  ContactIdEqualFilter: [],
  ContactIdNotEqualFilter: [],
  BandFilter: [],
  LengthRangeFilter: { IsFilterApplied: false, FromLength: 6, ToLength: 6 },
  SpecificTMTypeFilter: [],
  GenericTCTMTypeFilter: [],
  TMReceivedTypeFilter: [],
  SortByOption: { isApplied: false, ascending: false, byProperty: "TIMESTAMP" },
};

const fetchEntries = async (satelliteId, truetwinId, scenarioId, cursor) => {
  const tabSessionId = sessionStorage.getItem("sessionId");
  try {
    const { data } = await CommandCenterService.getAllRawCommandDetails(satelliteId, truetwinId, scenarioId, cursor, tabSessionId, RAW_BYTES_PAYLOAD);
    return { data: data || [], cursor: data?.[0]?.cursorId || "" };
  } catch (error) {
    console.error("Fetch error:", error);
    return { data: [], cursor: "" };
  }
};

const TableCell = memo(({ width, content, children }) => (
  <div className="d-flex align-items-center justify-content-start" style={{ width: `${width}px` }}>
    <CustomToolTip title={content} placement="top">
      <span className="truncate__text">{children ?? content}</span>
    </CustomToolTip>
  </div>
));

const Row = memo(({ index, style, data }) => {
  const { entries, selectedRow, show_selected_output_details, base64ToHex, isOutputModalVisible } = data;
  const entry = entries[index];
  if (!entry) return null;
  const isSelected = entry?.CommandUid === selectedRow?.CommandUid;

  return (
    <div style={style}>
      <div className="table__body__row d-flex">
        <button
          className={`table__body__row__item gap-3 ${(isSelected && isOutputModalVisible) ? "selected" : ""}`}
          onClick={() => show_selected_output_details(entry)}
          style={{ justifyContent: "flex-start" }}
        >
          <TableCell width={160} content={get_formated_time(entry?.["CommandDetails"]?.groundTimestamp / 1000)} />
          <TableCell width={850} content={base64ToHex(entry?.["CommandDetails"]?.completeFrame || "")} />
        </button>
      </div>
    </div>
  );
});

const RawBytesOutputList = memo(({ height, show_selected_output_details, selectedRow, isOutputModalVisible, entries, setEntries, base64ToHex }) => {
  const { scenario_id } = useUrlParams();
  const { selected_element } = useDashboard();
  const { trueTwinId, elementId } = selected_element || {};

  const listRef = useRef(null);
  const loadingRef = useRef(false);
  const cursorRef = useRef("");
  const isAtTopRef = useRef(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isDataFound, setIsDataFound] = useState(false);
  const isWsLoadingRef = useRef(false);
  const isWsPendingRef = useRef(false);
  const loadLatestEntriesRef = useRef(null);

  const loadLatestEntries = useCallback(
    async (fromWs) => {
      if (loadingRef.current) return;
      if (!trueTwinId || !elementId || !scenario_id) return;
      loadingRef.current = true;

      if (fromWs && isWsLoadingRef.current) {
        isWsPendingRef.current = true;
        loadingRef.current = false;
        return;
      }
      if (fromWs) isWsLoadingRef.current = true;

      try {
        const { data, cursor } = await fetchEntries(elementId, trueTwinId, scenario_id, "");
        setEntries(data);
        if (data) setIsDataFound(true);
        cursorRef.current = cursor;
        sessionStorage.setItem("globalCursor", cursor);
        if (isAtTopRef.current) listRef.current?.scrollToItem(0, "start");
      } finally {
        loadingRef.current = false;
        if (fromWs) {
          isWsLoadingRef.current = false;
          if (isWsPendingRef.current) {
            isWsPendingRef.current = false;
            loadLatestEntries(true);
          }
        }
      }
    },
    [elementId, trueTwinId, scenario_id, setEntries]
  );

  useEffect(() => { loadLatestEntriesRef.current = loadLatestEntries; }, [loadLatestEntries]);

  const loadMoreEntries = useCallback(async () => {
    if (loadingRef.current || !cursorRef.current) return;
    if (!trueTwinId || !elementId || !scenario_id) return;
    loadingRef.current = true;
    try {
      const { data, cursor } = await fetchEntries(elementId, trueTwinId, scenario_id, cursorRef.current);
      setEntries((prev) => [...prev, ...data]);
      cursorRef.current = cursor;
      sessionStorage.setItem("globalCursor", cursor);
    } finally {
      loadingRef.current = false;
    }
  }, [elementId, trueTwinId, scenario_id, setEntries]);

  const handleScroll = useCallback(
    ({ scrollOffset, scrollDirection }) => {
      setShowScrollTop(scrollOffset > 10);
      const list = listRef.current;
      if (!list) return;

      const totalHeight = entries.length * list.props.itemSize;
      const bottomThreshold = 5;

      if (scrollDirection === "forward" && scrollOffset + list.props.height >= totalHeight - bottomThreshold) {
        loadMoreEntries();
      } else if (scrollDirection === "backward" && scrollOffset === 0) {
        loadLatestEntries(false);
      }
      isAtTopRef.current = scrollOffset === 0;
    },
    [entries?.length, loadMoreEntries, loadLatestEntries]
  );

  useEffect(() => {
    const token = sessionStorage.getItem("authentication");
    if (!trueTwinId || !token) return;
    const sessionId = uuidv4();
    const ws = new WebSocket(`${getEnvVar("REACT_APP_WEBSOCKET_BACKEND_BASE_URL")}groundstation/websocketstatus?truetwinId=${trueTwinId}&sessionId=${sessionId}`);
    ws.onopen = () => ws.send(JSON.stringify({ type: "authorization", token }));
    ws.onmessage = () => isAtTopRef.current && loadLatestEntriesRef.current?.(true);
    ws.onerror = console.error;
    return () => ws.close();
  }, [trueTwinId, elementId]);

  useEffect(() => {
    loadLatestEntries(false);
  }, [trueTwinId, elementId, scenario_id]);

  const itemData = useMemo(
    () => ({
      entries,
      selectedRow,
      show_selected_output_details,
      base64ToHex,
      isOutputModalVisible,
    }),
    [entries, selectedRow, show_selected_output_details, base64ToHex, isOutputModalVisible]
  );

  return (
    <>
      {!isDataFound && <ShimmerMenuList />}
      <div className={`output__div__message__container ${((entries?.length === 0 || !entries) && isDataFound) ? "d-flex" : "d-none"}`}>
        No data found.
      </div>
      <List
        ref={listRef}
        height={height}
        itemCount={entries?.length || 0}
        itemSize={35}
        width="100%"
        onScroll={handleScroll}
        overscanCount={5}
        itemData={itemData}
        className="modal__scrollbar__white__vertical"
        style={{ overflowX: "hidden" }}
      >
        {Row}
      </List>
      <button
        onClick={() => {
          listRef.current?.scrollToItem(0, "start");
        }}
        title="Scroll to top"
        className={`scroll-to-top raw_byte_modal ${showScrollTop ? "" : "hidden"} ${isOutputModalVisible ? "" : "scroll-to-top-middle"}`}
      >
        <ArrowUp />
      </button>
    </>
  );
});

export default RawBytesOutputList;

